/**
 * OnboardingBanner — shown at the top of the allowed pages (My Profile,
 * Legal & Policies, Terms) while a new/reactivated employee has not yet
 * completed onboarding. Displays the 4 checks + a live progress count.
 *
 * When the gate is not active (grandfathered active user, admin, or already
 * complete), renders nothing.
 */
import { CheckCircle2, Circle, Lock, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';
import { ONBOARDING_LOCK_MESSAGE } from '../lib/onboarding';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

const CheckRow = ({ label, complete, hint, extra, testId, onClick }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={!onClick}
    className={cn(
      'w-full flex items-start gap-3 px-3 py-2.5 rounded-sm text-left',
      'transition-colors',
      onClick ? 'hover:bg-accent/40 cursor-pointer' : 'cursor-default',
    )}
  >
    {complete ? (
      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
    ) : (
      <Circle className="w-5 h-5 text-muted-foreground/60 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
    )}
    <div className="min-w-0 flex-1">
      <p className={cn(
        'text-sm font-medium uppercase tracking-wider',
        complete ? 'text-emerald-500' : 'text-foreground',
      )}>
        {label}
      </p>
      {hint ? (
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{hint}</p>
      ) : null}
      {extra}
    </div>
  </button>
);

export default function OnboardingBanner() {
  const { user } = useAuth();
  const { required, complete, checks, refresh } = useOnboarding();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  // Every time the banner mounts (i.e. user landed on My Profile / Legal /
  // Terms), re-fetch onboarding status so the checks reflect the latest
  // profile save / document upload / policy acknowledgement.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  // Only show while the gate is actively locking sections.
  if (!required || complete || !checks) {
    return null;
  }

  const doneCount = [
    checks.profile?.complete,
    checks.documents?.complete,
    checks.policies?.complete,
    checks.terms?.complete,
  ].filter(Boolean).length;

  const rolePrefix = user?.role === 'supervisor' ? '/supervisor' : '/employee';
  const goToProfile = () => navigate(`${rolePrefix}/my-profile`);
  const goToDocuments = () => navigate(`${rolePrefix}/my-profile?tab=documents`);
  const goToLegal = () => navigate(`${rolePrefix}/legal`);
  const goToTerms = () => navigate(`${rolePrefix}/legal?tab=terms`);

  const profileHint = checks.profile?.missing?.length
    ? `Missing: ${checks.profile.missing.slice(0, 3).join(', ')}${checks.profile.missing.length > 3 ? '…' : ''}`
    : 'All required fields complete';
  const docHint = checks.documents?.missing?.length
    ? `Missing: ${checks.documents.missing.slice(0, 2).join(', ')}${checks.documents.missing.length > 2 ? '…' : ''}`
    : (checks.documents?.expired?.length ? 'Some documents have expired — please re-upload' : 'All required documents uploaded');
  const polHint = (() => {
    const p = checks.policies?.missing_policy_count || 0;
    const pr = checks.policies?.missing_procedure_count || 0;
    if (p + pr === 0) return 'All policies acknowledged';
    const parts = [];
    if (p) parts.push(`${p} polic${p === 1 ? 'y' : 'ies'}`);
    if (pr) parts.push(`${pr} procedure${pr === 1 ? '' : 's'}`);
    return `${parts.join(' + ')} to acknowledge`;
  })();
  const termsHint = checks.terms?.complete
    ? 'Company terms accepted'
    : 'Read and accept the Company Terms & Conditions';

  return (
    <div
      className="mb-4 border border-amber-500/40 bg-amber-500/10 rounded-sm p-4 space-y-3"
      data-testid="onboarding-banner"
    >
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider" data-testid="onboarding-banner-title">
            Complete Onboarding to Unlock the App
          </p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {ONBOARDING_LOCK_MESSAGE}
          </p>
          <p className="text-xs text-muted-foreground mt-2" data-testid="onboarding-progress-count">
            <span className="font-medium text-foreground">{doneCount}</span> of 4 steps complete
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-xs text-amber-500 hover:text-amber-400 uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
          data-testid="onboarding-refresh-btn"
          title="Check my progress"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', refreshing && 'animate-spin')} strokeWidth={2} />
          Refresh
        </button>
      </div>
      <div className="space-y-1 border-t border-amber-500/20 pt-2">
        <CheckRow
          label="Profile Completed"
          complete={!!checks.profile?.complete}
          hint={profileHint}
          testId="onboarding-check-profile"
          onClick={goToProfile}
        />
        <CheckRow
          label="Documents Uploaded"
          complete={!!checks.documents?.complete}
          hint={docHint}
          extra={!checks.documents?.complete ? (
            <div className="mt-2 rounded-sm border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1" data-testid="onboarding-docs-howto">
              <p className="text-[11px] font-semibold text-amber-500 uppercase tracking-wider">How to upload your documents:</p>
              <ol className="text-[11px] text-muted-foreground leading-relaxed list-decimal list-inside space-y-0.5">
                <li>Tap this step — it opens the <strong className="text-foreground">Documents</strong> tab below</li>
                <li>Find each document marked <strong className="text-red-400">MISSING</strong></li>
                <li>Press the yellow <strong className="text-foreground">UPLOAD</strong> button next to it</li>
                <li>Choose a photo or PDF of your document (you can take a photo with your phone)</li>
                <li>Press <strong className="text-foreground">Upload</strong> — done. An admin will review it</li>
              </ol>
            </div>
          ) : null}
          testId="onboarding-check-documents"
          onClick={goToDocuments}
        />
        <CheckRow
          label="Company Policies Acknowledged"
          complete={!!checks.policies?.complete}
          hint={polHint}
          testId="onboarding-check-policies"
          onClick={goToLegal}
        />
        <CheckRow
          label="Terms & Conditions Accepted"
          complete={!!checks.terms?.complete}
          hint={termsHint}
          testId="onboarding-check-terms"
          onClick={goToTerms}
        />
      </div>
    </div>
  );
}
